import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import { alpha, Box, IconButton, Typography, useTheme } from '@mui/material';
import {
  DEFAULT_HF_RICH_TEXT_FORMATS,
  hfRichTextToHtml,
  htmlToHfRichText,
  type HfRichTextFormat,
  sanitizeRichTextHtml,
} from '../../utils/hfRichText';
import { composeSx } from '../../utils/composeSx';
import { hf, hfAlpha } from '../../theme';

export type HFWysiwygProps = {
  label?: string;
  /** HF rich-text markup (PUA delimiters), not HTML. */
  value: string;
  onChange: (value: string) => void;
  /** Toolbar inline formats. Default: bold, italic, underline. */
  formats?: HfRichTextFormat[];
  minRows?: number;
  /** Caps editor height; content scrolls when it exceeds this. Defaults to 12. */
  maxRows?: number;
  disabled?: boolean;
  placeholder?: string;
  /** Max characters of the HF markup `value` string (matches shared-contract maxLength). Shows a counter when set. */
  maxLength?: number;
  helperText?: string;
  error?: boolean;
};

const ROW_PX = 24;

const FORMAT_COMMAND: Record<HfRichTextFormat, string> = {
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
};

function exec(command: string) {
  document.execCommand(command, false);
}

export function HFWysiwyg({
  label,
  value,
  onChange,
  formats = DEFAULT_HF_RICH_TEXT_FORMATS,
  minRows = 6,
  maxRows = 12,
  disabled = false,
  placeholder,
  maxLength,
  helperText,
  error = false,
}: HFWysiwygProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const editorRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const formatsKey = formats.join(',');
  // Fingerprint via formatsKey so inline `formats={[...]}` from parents stays referentially stable.
  const enabledFormats = useMemo(() => (formatsKey ? (formatsKey.split(',') as HfRichTextFormat[]) : []), [formatsKey]);
  const htmlValue = useMemo(() => hfRichTextToHtml(value, enabledFormats), [value, enabledFormats]);
  const charLength = value.length;
  const overLimit = maxLength != null && charLength > maxLength;
  const showError = error || overLimit;
  const counterLabel = maxLength != null ? `${charLength} / ${maxLength}` : null;
  const resolvedHelper = helperText ?? (overLimit && maxLength != null ? `Exceeds the ${maxLength.toLocaleString()} character limit` : undefined);
  const resolvedMaxRows = Math.max(minRows, maxRows);

  useEffect(() => {
    if (focused || !editorRef.current) return;
    if (editorRef.current.innerHTML !== htmlValue) {
      editorRef.current.innerHTML = htmlValue || '';
    }
  }, [focused, htmlValue]);

  const emitChange = () => {
    const rawHtml = editorRef.current?.innerHTML ?? '';
    const safeHtml = sanitizeRichTextHtml(rawHtml, enabledFormats);
    if (editorRef.current && editorRef.current.innerHTML !== safeHtml) {
      editorRef.current.innerHTML = safeHtml;
    }
    onChange(htmlToHfRichText(safeHtml, enabledFormats));
  };

  const run = (format: HfRichTextFormat) => {
    exec(FORMAT_COMMAND[format]);
    emitChange();
  };

  const toolbarButtons: { format: HfRichTextFormat; label: string; icon: ReactElement }[] = [
    { format: 'bold', label: 'Bold', icon: <FormatBoldIcon fontSize="small" /> },
    { format: 'italic', label: 'Italic', icon: <FormatItalicIcon fontSize="small" /> },
    { format: 'underline', label: 'Underline', icon: <FormatUnderlinedIcon fontSize="small" /> },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {label ? (
        <Typography component="label" variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {label}
        </Typography>
      ) : null}
      <Box
        sx={{
          border: 1,
          borderColor: showError ? 'error.main' : focused ? 'primary.main' : isDark ? 'divider' : hfAlpha(hf.navy700, 0.32),
          borderRadius: 1,
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            px: 1,
            py: 0.75,
            borderBottom: 1,
            borderColor: isDark ? 'divider' : hfAlpha(hf.navy700, 0.24),
            bgcolor: isDark ? alpha(theme.palette.text.primary, 0.06) : hf.bgAlt,
          }}
        >
          {toolbarButtons
            .filter(({ format }) => enabledFormats.includes(format))
            .map(({ format, label: actionLabel, icon }) => (
              <IconButton
                key={format}
                size="small"
                aria-label={actionLabel}
                disabled={disabled}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => run(format)}
              >
                {icon}
              </IconButton>
            ))}
        </Box>
        <Box
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          data-placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onInput={emitChange}
          onBlur={() => {
            setFocused(false);
            emitChange();
          }}
          sx={composeSx({
            minHeight: minRows * ROW_PX,
            maxHeight: resolvedMaxRows * ROW_PX,
            overflowY: 'auto',
            px: 1.5,
            py: 1.25,
            color: 'text.primary',
            fontSize: 14,
            lineHeight: 1.55,
            outline: 'none',
            '&:empty::before': placeholder
              ? {
                  content: 'attr(data-placeholder)',
                  color: 'text.secondary',
                  pointerEvents: 'none',
                }
              : undefined,
            '& p': { m: 0, mb: 1 },
            '& p:last-child': { mb: 0 },
            '& u': { textDecoration: 'underline' },
          })}
        />
      </Box>
      {resolvedHelper || counterLabel ? (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, px: 0.25 }}>
          <Typography variant="caption" sx={{ color: showError ? 'error.main' : 'text.secondary', flex: '1 1 auto' }}>
            {resolvedHelper || ' '}
          </Typography>
          {counterLabel ? (
            <Typography
              variant="caption"
              aria-live="polite"
              sx={{ color: showError ? 'error.main' : 'text.secondary', flex: '0 0 auto', whiteSpace: 'nowrap' }}
            >
              {counterLabel}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
